import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosProgressEvent } from 'axios';
import FileUploader, { FileItem } from '../ui/uploader/file-uploader';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '../ui/input/input';
import { Button } from '../ui/button/button';
import { ScrollArea } from '../ui/scroll-area/scroll-area';
import { Svg90RingWithBg } from '@/icons/iconsComponent';
import { Textarea } from '../ui/text-area/text-area';
import { Label } from '../ui/label/label';
import { CreateTicketValidation } from '@/lib/validations/CreateTicketValidation';
import { CreateTicketRequest } from '@/models/support';
import { toast } from 'react-toastify';
import { ToastMsg } from '../ui/toast/toast-message';
import { InfoCircle } from 'iconsax-react';

export interface CreateTicketFormProps {
  onSubmit: (data: CreateTicketRequest) => void;
  onReset?: () => void;
  isLoading?: boolean;
  fileList: FileItem[];
  attachmentIds?: string[];
  onFileChange: (files: FileItem[]) => void;
  isUploading?: boolean;
  uploadFn?: (file: File, onProgress?: (progressEvent: AxiosProgressEvent) => void) => Promise<any>;
}

const CreateTicketForm = ({
  onSubmit,
  onReset,
  isLoading = false,
  fileList,
  attachmentIds,
  onFileChange,
  uploadFn,
  isUploading,
}: CreateTicketFormProps) => {
  const form = useForm<z.infer<typeof CreateTicketValidation>>({
    resolver: zodResolver(CreateTicketValidation),
    defaultValues: {
      subject: '',
      description: '',
    },
  });

  const scrollBottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
    return () => clearTimeout(timeout);
  }, [fileList.length]);

  const handleSubmit = (values: z.infer<typeof CreateTicketValidation>) => {
    if (isUploading) {
      toast.info(<ToastMsg message='لطفا تا پایان بارگذاری فایل صبر کنید.' />, {
        icon: <InfoCircle width={24} height={24} strokeWidth='2' className='text-orange' />,
        progressStyle: {
          backgroundColor: '#FF9800',
          height: '2px',
        },
        style: {
          padding: '15px',
        },
        delay: 300,
        closeButton: false,
      });
      return;
    }
    const ticketData: CreateTicketRequest = {
      description: values.description.trim(),
      ...(values.subject ? { subject: values.subject.trim() } : {}),
      attachmentIds: attachmentIds ?? [],
    };
console.log(ticketData);
console.log(attachmentIds);
    onSubmit(ticketData);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className='flex flex-col justify-between w-full pt-2 md:p-2 h-[calc(100svh-105px)] md:h-auto'
      >
        <ScrollArea className='h-[75svh] md:h-[482px]' dir='rtl'>
          <div className='flex flex-col gap-y-3 px-1 md:p-3 h-full'>
            <FormField
              name='subject'
              control={form.control}
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel className='body-14-medium text-text'>عنوان</FormLabel>
                  <FormControl>
                    <Input
                      data-testid='newticket-title'
                      {...field}
                      type='text'
                      placeholder='عنوان تیکت را وارد کنید'
                      disabled={isLoading}
                      className={`h-[34px]`}
                      maxLength={40}
                      autoComplete='off'
                    />
                  </FormControl>
                  <div className='flex justify-between items-center min-h-[20px]'>
                    <FormMessage className='body-12-regular text-red-1 !mt-0' />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              name='description'
              control={form.control}
              render={({ field }) => (
                <FormItem className='!w-full'>
                  <FormLabel className='body-14-medium text-text'>
                    متن پیام
                    <Label className='text-red-1 mr-1'>*</Label>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      data-testid='newticket-description'
                      {...field}
                      placeholder='پیام خود را بنویسید'
                      disabled={isLoading}
                      rows={6}
                      className={`w-full min-h-[138px] max-h-[138px] resize-none body-12-regular`}
                      maxLength={3000}
                    />
                  </FormControl>
                  <div className='flex justify-between items-center min-h-[20px]'>
                    <FormMessage className='body-12-regular text-red-1 !mt-0' />
                  </div>
                </FormItem>
              )}
            />

            <div className='flex flex-col gap-2'>
              <FileUploader
                fileList={fileList}
                onChange={onFileChange}
                maxCount={3}
                maxSize={3}
                multiple={true}
                accept='.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.heic,.tiff'
                uploadFn={uploadFn}
                uploaderTitle='بارگذاری فایل'
                disabled={isLoading}
              />
            </div>
            <p className='!text-icons body-10-regular'>
              * فرمت های مجاز: JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX, TXT, HEIC, TIFF
            </p>
            <p className='!text-icons body-10-regular'>* سایز مجاز: حداکثر 3 مگابایت</p>
            <div ref={scrollBottomRef} />
          </div>
        </ScrollArea>
        <div className='flex justify-center items-center w-full md:w-auto gap-3 pt-[20px] pb-[12px] md:mt-2 md:border-t md:border-border border-background md:-mx-[8px]'>
          <Button
            data-testid='newticket-submit'
            type='submit'
            disabled={isLoading}
            className='md:px-[32px] md:py-[8px] h-[60px] md:h-auto w-full md:w-auto items-center gap-2 md:mx-0'
          >
            {isLoading && <Svg90RingWithBg className='text-pics scale-[0.6]' />}
            ارسال
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateTicketForm;
